<!-- 2 -->


<template>
	<div class="auth-page" :style="authPageStyle">
		<!-- Левая часть с изображением -->
		<div
			v-if="group231"
			class="auth-image"
			:style="{ backgroundImage: `url(${group231})` }"
			aria-hidden="true"
		></div>

		<!-- Правая часть с формами -->
		<div class="auth-container">
			<!-- Кнопка возврата -->
			<router-link 
				to="/" 
				class="back-button"
				aria-label="Вернуться на главную"
			>
				<div class="back-circle"></div>
				<img
					class="back-arrow"
					src="@/assets/cdn-animaapp/arrow-2-2.svg"
					alt="Назад"
				/>
			</router-link>

			<div class="auth-content">
				<!-- Заголовок -->
				<h1 class="auth-title poppins-bold-black-24px-2">Добро пожаловать!</h1>

				<!-- Переключатель табов -->
				<div class="tab-switcher" role="tablist">
					<div
						class="tab-indicator"
						:style="{
							transform: activeTab === 'registration' ? 'translateX(100%)' : 'translateX(0%)',
						}"
					></div>
					<button
						type="button"
						role="tab"
						:aria-selected="activeTab === 'login'"
						:tabindex="activeTab === 'login' ? 0 : -1"
						@click="switchTab('login')"
						class="tab-button poppins-medium-white-16px"
						:class="{ active: activeTab === 'login' }"
					>
						Вход
					</button>
					<button
						type="button"
						role="tab"
						:aria-selected="activeTab === 'registration'"
						:tabindex="activeTab === 'registration' ? 0 : -1"
						@click="switchTab('registration')"
						class="tab-button poppins-medium-white-16px"
						:class="{ active: activeTab === 'registration' }"
					>
						Регистрация
					</button>
				</div>

				<!-- Формы -->
				<transition name="fade" mode="out-in">
					<!-- Форма входа -->
					<form 
						v-if="activeTab === 'login'" 
						key="login" 
						@submit.prevent="handleLogin"
						novalidate
					>
						<div class="form-fields">
							<p class="form-description poppins-normal-chicago-14px">
								{{ text127 }}
							</p>

							<!-- Поле логина -->
							<div class="input-wrapper">
								<Group232
									:email1="group232Props?.email1"
									:email2="group232Props?.email2"
									:className="group232Props?.className"
									type="text"
									autocomplete="username"
									v-model="login.username.value"
									:class="getInputClasses('login', 'username')"
									:aria-invalid="!!login.errors.value.username"
									:aria-describedby="login.errors.value.username ? 'login-username-error' : undefined"
									@focus="login.focused.value.username = true"
									@blur="handleBlur('login', 'username')"
									@input="login.validateField('username')"
									@keyup.enter="focusNextField('loginPassword')"
								/>
								<transition name="error-slide">
									<div 
										v-if="login.errors.value.username" 
										id="login-username-error"
										class="error-message"
										role="alert"
										aria-live="polite"
									>
										{{ login.errors.value.username }}
									</div>
								</transition>
							</div>

							<!-- Поле пароля -->
							<div class="input-wrapper">
								<Group227
									ref="loginPassword"
									:group232Props="group227Props?.group232Props"
									v-model="login.password.value"
									:class="getInputClasses('login', 'password')"
									:aria-invalid="!!login.errors.value.password"
									:aria-describedby="login.errors.value.password ? 'login-password-error' : undefined"
									@focus="login.focused.value.password = true"
									@blur="handleBlur('login', 'password')"
									@input="login.validateField('password')"
									@keyup.enter="handleLogin"
								/>
								<transition name="error-slide">
									<div 
										v-if="login.errors.value.password" 
										id="login-password-error"
										class="error-message"
										role="alert"
										aria-live="polite"
									>
										{{ login.errors.value.password }}
									</div>
								</transition>
							</div>

							<!-- Капча: перенесена в модальное окно -->
						</div>

						<RememberMe
							v-model="login.rememberMe.value"
							:current-login="login.username.value"
							:remember-label="text128"
							:forgot-label="text129"
							@select-login="onSelectRememberedLogin"
							@forgot-password="handleForgotPassword"
						/>

						<button
							type="submit"
							class="submit-button"
							:class="{ 'is-loading': login.isSubmitting.value }"
							:disabled="login.isSubmitting.value"
						>
							<span class="button-text poppins-bold-white-16px">
								{{ login.isSubmitting.value ? 'Вход...' : text130 }}
							</span>
						</button>
					</form>

					<!-- Форма регистрации -->
					<form 
						v-else 
						key="registration" 
						class="auth-form--register"
						@submit.prevent="handleRegister"
						novalidate
					>
						<p class="form-description poppins-normal-chicago-14px">{{ text10 }}</p>

						<!-- Email -->
						<div class="input-wrapper">
							<Group232
								:email1="group2321Props?.email1"
								:email2="group2321Props?.email2"
								type="email"
								autocomplete="email"
								v-model="register.email.value"
								:class="getInputClasses('register', 'email')"
								:aria-invalid="!!register.errors.value.email"
								:aria-describedby="register.errors.value.email ? 'reg-email-error' : undefined"
								@focus="register.focused.value.email = true"
								@blur="handleBlur('register', 'email')"
								@input="register.validateField('email')"
								@keyup.enter="focusNextField('regLastName')"
							/>
							<transition name="error-slide">
								<div 
									v-if="register.errors.value.email" 
									id="reg-email-error"
									class="error-message"
									role="alert"
									aria-live="polite"
								>
									{{ register.errors.value.email }}
								</div>
							</transition>
						</div>

						
					<!-- Name fields -->
					<div class="name-grid">
						<div class="input-wrapper">
							<Group232
								ref="regLastName"
								email1="Фамилия"
								email2="Введите фамилию"
								type="text"
								autocomplete="family-name"
								v-model="register.lastName.value"
								:class="getInputClasses('register', 'lastName')"
								:aria-invalid="!!register.errors.value.lastName"
								:aria-describedby="register.errors.value.lastName ? 'reg-lastname-error' : undefined"
								@focus="register.focused.value.lastName = true"
								@blur="handleBlur('register', 'lastName')"
								@input="register.validateField('lastName')"
								@keyup.enter="focusNextField('regFirstName')"
							/>
							<transition name="error-slide">
								<div
									v-if="register.errors.value.lastName"
									id="reg-lastname-error"
									class="error-message"
									role="alert"
									aria-live="polite"
								>
									{{ register.errors.value.lastName }}
								</div>
							</transition>
						</div>

						<div class="input-wrapper">
							<Group232
								ref="regFirstName"
								email1="Имя"
								email2="Введите имя"
								type="text"
								autocomplete="given-name"
								v-model="register.firstName.value"
								:class="getInputClasses('register', 'firstName')"
								:aria-invalid="!!register.errors.value.firstName"
								:aria-describedby="register.errors.value.firstName ? 'reg-firstname-error' : undefined"
								@focus="register.focused.value.firstName = true"
								@blur="handleBlur('register', 'firstName')"
								@input="register.validateField('firstName')"
								@keyup.enter="focusNextField('regPatronymic')"
							/>
							<transition name="error-slide">
								<div
									v-if="register.errors.value.firstName"
									id="reg-firstname-error"
									class="error-message"
									role="alert"
									aria-live="polite"
								>
									{{ register.errors.value.firstName }}
								</div>
							</transition>
						</div>

						<div class="input-wrapper input-wrapper--full">
							<Group232
								ref="regPatronymic"
								email1="Отчество"
								email2="Необязательно"
								type="text"
								autocomplete="additional-name"
								v-model="register.patronymic.value"
								:class="getInputClasses('register', 'patronymic')"
								:aria-invalid="!!register.errors.value.patronymic"
								:aria-describedby="register.errors.value.patronymic ? 'reg-patronymic-error' : undefined"
								@focus="register.focused.value.patronymic = true"
								@blur="handleBlur('register', 'patronymic')"
								@input="register.validateField('patronymic')"
								@keyup.enter="focusNextField('regPassword')"
							/>
							<transition name="error-slide">
								<div
									v-if="register.errors.value.patronymic"
									id="reg-patronymic-error"
									class="error-message"
									role="alert"
									aria-live="polite"
								>
									{{ register.errors.value.patronymic }}
								</div>
							</transition>
						</div>
					</div>

					<!-- Password -->
						<div class="input-wrapper">
							<Group227
								ref="regPassword"
								:group232Props="group2271Props?.group232Props"
								v-model="register.password.value"
								:class="getInputClasses('register', 'password')"
								:aria-invalid="!!register.errors.value.password"
								:aria-describedby="register.errors.value.password ? 'reg-password-error' : undefined"
								@focus="register.focused.value.password = true"
								@blur="handleBlur('register', 'password')"
								@input="register.validateField('password')"
								@keyup.enter="focusNextField('regConfirmPassword')"
							/>
							<!-- Индикатор силы пароля -->
							<div 
								v-if="register.password.value && !register.errors.value.password" 
								class="password-strength"
							>
								<div 
									class="strength-bar"
									:style="{ 
										width: `${(register.passwordStrength.value.score / 7) * 100}%`,
										backgroundColor: register.passwordStrength.value.color 
									}"
								></div>
								<span 
									class="strength-label"
									:style="{ color: register.passwordStrength.value.color }"
								>
									{{ register.passwordStrength.value.label }}
								</span>
							</div>
							<transition name="error-slide">
								<div 
									v-if="register.errors.value.password" 
									id="reg-password-error"
									class="error-message"
									role="alert"
									aria-live="polite"
								>
									{{ register.errors.value.password }}
								</div>
							</transition>
						</div>

						<!-- Подтверждение пароля -->
						<div class="input-wrapper">
							<Group227
								ref="regConfirmPassword"
								:group232Props="group2272Props?.group232Props"
								v-model="register.confirmPassword.value"
								:class="getInputClasses('register', 'confirmPassword')"
								:aria-invalid="!!register.errors.value.confirmPassword"
								:aria-describedby="register.errors.value.confirmPassword ? 'reg-confirm-error' : undefined"
								@focus="register.focused.value.confirmPassword = true"
								@blur="handleBlur('register', 'confirmPassword')"
								@input="register.validateField('confirmPassword')"
								@keyup.enter="handleRegister"
							/>
							<transition name="error-slide">
								<div 
									v-if="register.errors.value.confirmPassword" 
									id="reg-confirm-error"
									class="error-message"
									role="alert"
									aria-live="polite"
								>
									{{ register.errors.value.confirmPassword }}
								</div>
							</transition>
						</div>

						<button
							type="submit"
							class="submit-button submit-button--register"
							:class="{ 'is-loading': register.isSubmitting.value }"
							:disabled="register.isSubmitting.value"
						>
							<span class="button-text poppins-bold-white-16px">
								{{ register.isSubmitting.value ? 'Регистрация...' : text11 }}
							</span>
						</button>
					</form>
				</transition>
			</div>
		</div>

		<!-- Модальное окно восстановления пароля -->
		<ForgotPasswordModal
			:visible="showForgotPasswordModal"
			@close="closeForgotPasswordModal"
			@success="onPasswordResetSuccess"
		/>

		<!-- Модальное окно МФА при логине -->
		<MfaLoginModal
			v-if="showMfaModal"
			:userId="mfaUserId"
			:email="mfaEmail"
			@close="showMfaModal = false"
			@success="handleMfaSuccess"
		/>

		<!-- Капча как модальное окно -->
		<transition name="fade">
			<div
				v-if="captchaRequired"
				class="captcha-modal__overlay"
			>
				<div class="captcha-modal__dialog" role="dialog" aria-modal="true">
					<h2 class="captcha-modal__title">Капча</h2>
					<p class="captcha-modal__hint">Введите число для строки:</p>
					<div class="captcha-modal__question">{{ captchaQuestion }}</div>

					<input
						ref="captchaInput"
						class="captcha-modal__input"
						type="text"
						inputmode="numeric"
						autocomplete="off"
						placeholder="Например: 1234"
						:value="captchaAnswer"
						@input="onCaptchaInput"
						@keyup.enter="verifyCaptcha"
					/>

					<div class="captcha-modal__actions">
						<button
							type="button"
							class="captcha-modal__btn captcha-modal__btn--ghost"
							:disabled="captchaLoading"
							@click="fetchCaptcha"
						>
							{{ captchaLoading ? 'Обновление...' : 'Обновить' }}
						</button>
						<button
							type="button"
							class="captcha-modal__btn captcha-modal__btn--primary"
							:disabled="captchaLoading || !captchaAnswer"
							@click="verifyCaptcha"
						>
							Продолжить
						</button>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import { ref } from 'vue'
import Group232 from './Group232'
import Group227 from './Group227'
import RememberMe from './RememberMe'
import ForgotPasswordModal from './ForgotPasswordModal.vue'
import MfaLoginModal from './MfaLoginModal.vue'
import { useLoginForm, useRegisterForm, debounce } from '@/composables/useFormValidation'
import { authService } from '@/services/auth'
import { useAppStore } from '@/stores/useAppStore'

export default {
	name: 'AuthPage',
	components: {
		Group232,
		Group227,
		RememberMe,
		ForgotPasswordModal,
		MfaLoginModal,
	},
	props: {
		group231: { type: String, default: '' },
		text127: { type: String, default: '' },
		text128: { type: String, default: 'Запомнить меня' },
		text129: { type: String, default: 'Забыли пароль?' },
		text130: { type: String, default: 'Войти' },
		group232Props: { type: Object, default: () => ({}) },
		group227Props: { type: Object, default: () => ({}) },
		text10: { type: String, default: '' },
		text11: { type: String, default: 'Зарегистрироваться' },
		group2321Props: { type: Object, default: () => ({}) },
		group2322Props: { type: Object, default: () => ({}) },
		group2271Props: { type: Object, default: () => ({}) },
		group2272Props: { type: Object, default: () => ({}) },
	},

	data() {
		return {
			viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
			viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 1080,
		}
	},

	computed: {
		authPageStyle() {
			if (this.viewportWidth < 1024) return null

			const scale = this.viewportWidth / 1920
			if (!Number.isFinite(scale) || scale <= 0) return null

			return {
				transform: `scale(${scale})`,
				transformOrigin: 'top left',
				height: `${this.viewportHeight / scale}px`,
			}
		},
	},

	setup() {
		const activeTab = ref('login')
		const appStore = useAppStore()

		// Composables для форм
		const login = useLoginForm()
		const register = useRegisterForm()

		// Капча для логина (включается сервером при превышении лимитов)
		const captchaRequired = ref(false)
		const captchaId = ref('')
		const captchaQuestion = ref('')
		const captchaAnswer = ref('')
		const captchaLoading = ref(false)
		const captchaInput = ref(null)

		// Refs для фокуса
		const loginPassword = ref(null)
		const regLastName = ref(null)
		const regFirstName = ref(null)
		const regPatronymic = ref(null)
		const regPassword = ref(null)
		const regConfirmPassword = ref(null)

		// Модальное окно восстановления пароля
		const showForgotPasswordModal = ref(false)

		// МФА модалка
		const showMfaModal = ref(false)
		const mfaUserId = ref(null)
		const mfaEmail = ref('')

		// Debounced submit для защиты от спама
		const debouncedLoginSubmit = debounce(() => {}, 1000)
		const debouncedRegisterSubmit = debounce(() => {}, 1000)

		return {
			activeTab,
			appStore,
			login,
			register,
			captchaRequired,
			captchaId,
			captchaQuestion,
			captchaAnswer,
			captchaLoading,
			captchaInput,
			loginPassword,
			regLastName,
			regFirstName,
			regPatronymic,
			regPassword,
			regConfirmPassword,
			showForgotPasswordModal,
			showMfaModal,
			mfaUserId,
			mfaEmail,
			debouncedLoginSubmit,
			debouncedRegisterSubmit,
		}
	},

	mounted() {
		this.updateViewportMetrics()
		window.addEventListener('resize', this.updateViewportMetrics)

		// Проверяем URL параметр
		if (this.$route?.query?.tab === 'registration') {
			this.activeTab = 'registration'
		}

		// Автофокус на первое поле
		this.$nextTick(() => {
			this.focusFirstField()
		})
	},

	beforeUnmount() {
		window.removeEventListener('resize', this.updateViewportMetrics)
	},

	methods: {
		updateViewportMetrics() {
			if (typeof window === 'undefined') return

			this.viewportWidth = window.innerWidth
			this.viewportHeight = window.innerHeight
		},

		/**
		 * Переключение табов
		 */
		switchTab(tab) {
			if (this.activeTab === tab) return
			this.activeTab = tab
			
			// Очищаем обе формы
			this.login.reset()
			this.register.reset()
			this.captchaRequired = false
			this.captchaId = ''
			this.captchaQuestion = ''
			this.captchaAnswer = ''

			// Фокус на первое поле новой формы
			this.$nextTick(() => {
				this.focusFirstField()
			})
		},

		/**
		 * Фокус на первое поле текущей формы
		 */
		focusFirstField() {
			const selector = this.activeTab === 'login' 
				? '.auth-content form input:first-of-type'
				: '.auth-content form input[type="email"]'
			const input = this.$el?.querySelector(selector)
			input?.focus()
		},

		/**
		 * Фокус на следующее поле
		 */
		focusNextField(refName) {
			this.$nextTick(() => {
				const ref = this.$refs[refName]
				if (ref) {
					const input = ref.$el?.querySelector('input') || ref.$el
					input?.focus()
				}
			})
		},

		/**
		 * Классы для полей ввода
		 */
		getInputClasses(form, field) {
			const formData = form === 'login' ? this.login : this.register
			const error = formData.errors.value[field]
			const focused = formData.focused.value[field]
			const value = formData[field]?.value

			return {
				'input-error': !!error,
				'input-focus': focused,
				'input-valid': value && !error,
			}
		},

		/**
		 * Обработка blur
		 */
		handleBlur(form, field) {
			const formData = form === 'login' ? this.login : this.register
			formData.focused.value[field] = false
			formData.validateFieldImmediate(field)
		},

		/**
		 * Выбор сохранённого логина
		 */
		onSelectRememberedLogin(loginValue) {
			this.login.username.value = loginValue
			this.login.validateField('username')
		},

		/**
		 * Забыли пароль
		 */
		handleForgotPassword() {
			this.showForgotPasswordModal = true
		},

		/**
		 * Закрытие модального окна восстановления пароля
		 */
		closeForgotPasswordModal() {
			this.showForgotPasswordModal = false
		},

		/**
		 * Успешное восстановление пароля
		 */
		onPasswordResetSuccess() {
			this.appStore.showSuccess('Пароль успешно изменен. Войдите с новым паролем')
		},

		/**
		 * Авторизация
		 */
		async handleLogin() {
			if (this.login.isSubmitting.value) return
			if (this.captchaRequired) {
				this.appStore.showWarning('Сначала подтвердите капчу')
				this.$nextTick(() => {
					this.captchaInput?.focus?.()
				})
				return
			}

			const validationError = this.login.validateForm()
			if (validationError) {
				this.appStore.showWarning(validationError)
				return
			}

			this.login.isSubmitting.value = true

			try {
				// 🔍 ОТЛАДКА: логируем значения перед отправкой
				console.log('🔍 [AuthPage.handleLogin] Raw username:', this.login.username.value)
				console.log('🔍 [AuthPage.handleLogin] Trimmed username:', this.login.trimmedUsername.value)
				console.log('🔍 [AuthPage.handleLogin] Username has dot:', this.login.trimmedUsername.value.includes('.'))
				
				const result = await authService.login(
					this.login.trimmedUsername.value,
					this.login.password.value,
					{
						captchaId: null,
						captchaAnswer: null,
					}
				)

				// Проверка на требование МФА
				if (result.requiresMfa) {
					this.showMfaModal = true
					this.mfaUserId = result.userId
					this.mfaEmail = result.email
					return
				}

				// Обычный вход без МФА
				const { user } = result

				// Роутинг на основе роли
				const roles = Array.isArray(user?.roles) ? user.roles : []

				let targetRoute = '/student-dashboard'

				if (roles.includes('admin')) {
					targetRoute = '/admin/dashboard'
				} else if (roles.includes('teacher')) {
					targetRoute = '/teacher/dashboard'
				} else if (roles.includes('student')) {
					targetRoute = '/student-dashboard'
				}

				await this.$router.push(targetRoute)
			} catch (error) {
				const code = error?.response?.data?.error?.code
				if (error?.response?.status === 429 && code === 'CAPTCHA_REQUIRED') {
					this.captchaRequired = true
					this.appStore.showWarning('Введите капчу, чтобы продолжить вход')
					await this.fetchCaptcha()
					this.$nextTick(() => {
						this.captchaInput?.focus?.()
					})
					return
				}

				// authService уже показывает ошибку
				if (process.env.NODE_ENV !== 'production') {
					console.error('❌ [AuthPage] Login error:', error)
				}
			} finally {
				this.login.isSubmitting.value = false
			}
		},

		async fetchCaptcha() {
			if (this.captchaLoading) return
			this.captchaLoading = true
			try {
				const email = this.login.trimmedUsername.value
				const captcha = await authService.getLoginCaptcha(email)
				this.captchaId = captcha.captchaId || ''
				this.captchaQuestion = captcha.question || ''
				this.captchaAnswer = ''
				this.$nextTick(() => {
					this.captchaInput?.focus?.()
				})
			} catch (error) {
				if (process.env.NODE_ENV !== 'production') {
					console.error('❌ [AuthPage] Captcha error:', error)
				}
				this.appStore.showError('Не удалось получить капчу. Попробуйте позже')
			} finally {
				this.captchaLoading = false
			}
		},

		async verifyCaptcha() {
			if (this.captchaLoading) return

			if (!String(this.captchaAnswer || '').trim()) {
				this.appStore.showWarning('Введите число из капчи')
				this.$nextTick(() => {
					this.captchaInput?.focus?.()
				})
				return
			}

			this.captchaLoading = true

			try {
				await authService.verifyCaptcha(
					this.login.trimmedUsername.value,
					this.captchaId,
					this.captchaAnswer
				)

				// Капча верна - закрываем модалку
				this.appStore.showSuccess('Капча подтверждена. Теперь можете войти')
				this.closeCaptchaModal()
			} catch (error) {
				// Капча неверна - показываем ошибку и обновляем капчу
				const message = error?.response?.data?.error?.message || 'Неверная капча'
				this.appStore.showError(message)
				
				// Сбрасываем флаг перед обновлением капчи
				this.captchaLoading = false
				
				// Автоматически обновляем капчу
				await this.fetchCaptcha()
			} finally {
				this.captchaLoading = false
			}
		},

		closeCaptchaModal() {
			this.captchaRequired = false
			this.captchaId = ''
			this.captchaQuestion = ''
			this.captchaAnswer = ''
		},

		onCaptchaInput(event) {
			const value = String(event?.target?.value || '')
			this.captchaAnswer = value.replace(/\D+/g, '')
		},

		/**
		 * Успешная МФА аутентификация
		 */
		async handleMfaSuccess() {
			this.showMfaModal = false
			
			const user = this.appStore.currentUser
			const roles = Array.isArray(user?.roles) ? user.roles : []

			let targetRoute = '/student-dashboard'

			if (roles.includes('admin')) {
				targetRoute = '/admin/dashboard'
			} else if (roles.includes('teacher')) {
				targetRoute = '/teacher/dashboard'
			} else if (roles.includes('student')) {
				targetRoute = '/student-dashboard'
			}

			await this.$router.push(targetRoute)
		},

		/**
		 * Регистрация
		 */
		async handleRegister() {
			if (this.register.isSubmitting.value) return

			const validationError = this.register.validateForm()
			if (validationError) {
				this.appStore.showWarning(validationError)
				return
			}

			this.register.isSubmitting.value = true

			try {
				const response = await authService.register({
					email: this.register.trimmedEmail.value,
					password: this.register.password.value,
					full_name: this.register.trimmedFullName.value,
					role: 'student',
				})

				// Очищаем пароли
				this.register.clearPasswords()

				// Проверяем, требуется ли верификация email
				if (response.data.requiresEmailVerification) {
					this.appStore.showSuccess('Код верификации отправлен на email')
					await this.$router.push({
						path: '/verify-email',
						query: { email: this.register.trimmedEmail.value },
					})
				} else {
					// Преподаватели сразу попадают в систему
					this.appStore.showSuccess('Регистрация успешна! Добро пожаловать!')
					await this.$router.push('/student-dashboard')
				}
			} catch (error) {
				if (process.env.NODE_ENV !== 'production') {
					console.error('❌ [AuthPage] Registration error:', error)
				}
				// Fallback если authService не показал ошибку
				if (!error.response) {
					this.appStore.showError('Не удалось завершить регистрацию. Попробуйте ещё раз.')
				}
			} finally {
				this.register.isSubmitting.value = false
			}
		},
	},
}
</script>

<style scoped>
/* ========== ОСНОВНОЙ LAYOUT ========== */
/* Фиксированный размер под 1920x1080, масштабируется на меньших экранах */
.auth-page {
	background-color: var(--romance, #f5f5f5);
	min-height: 900px;
	min-width: 1440px;
	width: 1920px;
	position: relative;
	display: flex;
	justify-content: center;
	align-items: flex-start;
}

/* Изображение слева — фиксированные размеры */
.auth-image {
	background-size: 100% 100%;
	height: 825px;
	left: 200px;
	position: absolute;
	top: 37px;
	width: 737px;
}

/* Контейнер формы — фиксированное позиционирование */
.auth-container {
	position: absolute;
	left: calc(50% + 100px);
	top: 70px;
	width: 513px;
}

.auth-content {
	display: flex;
	flex-direction: column;
	margin-left: 60px;
	width: 453px;
}

/* ========== CAPTCHA MODAL ========== */
.captcha-modal__overlay {
	position: fixed;
	inset: 0;
	background: rgba(15, 23, 42, 0.55);
	backdrop-filter: blur(4px);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 18px;
	z-index: 9999;
}

.captcha-modal__dialog {
	width: 100%;
	max-width: 520px;
	background: #ffffff;
	border-radius: 18px;
	border: 1px solid rgba(15, 23, 42, 0.12);
	box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
	padding: 18px 18px 16px;
}

.captcha-modal__title {
	margin: 0 0 6px;
	font-size: 18px;
	font-weight: 700;
	color: #0f172a;
}

.captcha-modal__hint {
	margin: 0 0 10px;
	font-size: 13px;
	line-height: 1.4;
	color: rgba(15, 23, 42, 0.75);
}

.captcha-modal__question {
	font-family: 'Montserrat', Helvetica, sans-serif;
	font-size: 15px;
	font-weight: 700;
	letter-spacing: 0.2px;
	color: #0f172a;
	background: #f8fafc;
	border: 1px solid rgba(15, 23, 42, 0.12);
	border-radius: 12px;
	padding: 12px 14px;
	word-break: break-word;
}

.captcha-modal__input {
	margin-top: 12px;
	width: 100%;
	height: 44px;
	border-radius: 12px;
	border: 1px solid rgba(15, 23, 42, 0.18);
	padding: 0 12px;
	font-size: 16px;
	outline: none;
}

.captcha-modal__input:focus {
	border-color: #2563eb;
	box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.15);
}

.captcha-modal__actions {
	margin-top: 14px;
	display: flex;
	gap: 10px;
	justify-content: flex-end;
	flex-wrap: wrap;
}

.captcha-modal__btn {
	height: 40px;
	padding: 0 14px;
	border-radius: 12px;
	font-size: 14px;
	font-weight: 700;
	cursor: pointer;
}

.captcha-modal__btn:disabled {
	opacity: 0.6;
	cursor: default;
}

.captcha-modal__btn--ghost {
	background: #ffffff;
	border: 1px solid rgba(15, 23, 42, 0.18);
	color: #0f172a;
}

.captcha-modal__btn--primary {
	background: linear-gradient(120deg, #1d4ed8 0%, #0284c7 100%);
	border: 0;
	color: #ffffff;
}

/* ========== КНОПКА НАЗАД ========== */
.back-button {
	position: absolute;
	top: -2px;
	left: 0;
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	text-decoration: none;
	transition: transform 0.3s ease;
}

.back-button:hover {
	transform: scale(1.15);
}

.back-button:focus-visible {
	outline: 2px solid var(--azure-radiance, #007bff);
	outline-offset: 2px;
	border-radius: 50%;
}

.back-circle {
	position: absolute;
	inset: 0;
	border: 1px solid var(--aqua-pearl, #ccc);
	border-radius: 50%;
	transition: all 0.3s ease;
}

.back-button:hover .back-circle {
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	border-width: 2px;
}

.back-arrow {
	width: 21px;
	height: 15px;
	position: relative;
	z-index: 1;
}

/* ========== ЗАГОЛОВОК ========== */
.auth-title {
	text-align: center;
	margin-bottom: clamp(1rem, 3vh, 28px);
	font-size: clamp(1.25rem, 4vw, 1.5rem);
}

/* ========== ТАБЫ ========== */
.tab-switcher {
	position: relative;
	display: flex;
	align-items: center;
	width: 100%;
	max-width: 329px;
	height: 59px;
	margin: 0 auto clamp(1rem, 2vh, 18px);
	padding: 0;
	background-color: var(--azure-radiance-5, rgba(0, 123, 255, 0.05));
	border-radius: 33px;
	overflow: hidden;
}

.tab-indicator {
	position: absolute;
	top: 10px;
	bottom: 10px;
	left: 13px;
	width: calc(50% - 13px);
	background-color: var(--azure-radiance, #007bff);
	border-radius: 33px;
	transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	z-index: 0;
}

.tab-button {
	flex: 1;
	height: 100%;
	background: transparent;
	border: none;
	cursor: pointer;
	position: relative;
	z-index: 1;
	color: var(--white, #fff);
	transition: color 0.3s ease, opacity 0.2s ease;
}

.tab-button:focus-visible {
	outline: 2px solid var(--azure-radiance, #007bff);
	outline-offset: -4px;
	border-radius: 33px;
}

/* ========== ФОРМЫ ========== */
.form-fields {
	display: flex;
	flex-direction: column;
	margin-top: clamp(1rem, 2vh, 18px);
}

.form-description {
	text-align: center;
	margin-bottom: 1rem;
}

/* ========== ПОЛЯ ВВОДА ========== */
.input-wrapper {
	position: relative;
	margin-bottom: 0;
	min-height: 90px;
}

.name-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	column-gap: 14px;
	row-gap: 10px;
	margin-top: 6px;
}

.input-wrapper--full {
	grid-column: 1 / -1;
}

:deep(.auth-form--register .group-232),
:deep(.auth-form--register .group-2),
:deep(.auth-form--register .group-227) {
	margin-left: 0;
	max-width: 100%;
}

:deep(.auth-form--register .rectangle-80),
:deep(.auth-form--register .input-field) {
	max-width: 100%;
}

:deep(.name-grid .group-232) {
	margin-left: 0;
	margin-top: 0;
	max-width: 100%;
}

:deep(.name-grid .rectangle-80),
:deep(.name-grid .input-field) {
	max-width: 100%;
}


.error-message {
	position: absolute;
	bottom: -20px;
	left: 22px;
	color: #ff4444;
	font-size: 11px;
	display: flex;
	align-items: center;
	gap: 4px;
	white-space: nowrap;
	z-index: 10;
	font-weight: 500;
}

.error-message::before {
	content: '⚠';
	font-size: 13px;
}

/* Анимация ошибок */
.error-slide-enter-active,
.error-slide-leave-active {
	transition: opacity 0.3s ease-out, transform 0.3s ease-out;
}

.error-slide-enter-from,
.error-slide-leave-to {
	opacity: 0;
	transform: translateY(-5px);
}

/* Подсветка полей */
:deep(.input-error .rectangle-80) {
	border: 2px solid #ff4444 !important;
	background-color: #fff5f5 !important;
}

:deep(.input-focus .rectangle-80) {
	border: 2px solid var(--azure-radiance, #007bff) !important;
	box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

:deep(.input-valid .rectangle-80) {
	border: 2px solid #4caf50 !important;
	background-color: #f1f8f4 !important;
}

/* Анимация shake */
:deep(.input-error .group-232),
:deep(.input-error .group-2),
:deep(.input-error .group-227) {
	animation: shake 0.5s;
}

@keyframes shake {
	0%, 100% { transform: translateX(0); }
	10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
	20%, 40%, 60%, 80% { transform: translateX(5px); }
}

:deep(.rectangle-80) {
	transition: all 0.3s ease;
}

/* ========== ИНДИКАТОР СИЛЫ ПАРОЛЯ ========== */
.password-strength {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 4px;
	padding: 0 22px;
}

.strength-bar {
	height: 4px;
	border-radius: 2px;
	transition: width 0.3s ease, background-color 0.3s ease;
	min-width: 20px;
}

.strength-label {
	font-size: 11px;
	font-weight: 500;
	transition: color 0.3s ease;
}

/* ========== КНОПКИ SUBMIT ========== */
.submit-button {
	display: flex;
	align-items: center;
	justify-content: center;
	width: clamp(180px, 50%, 232px);
	height: 49px;
	margin-left: auto;
	margin-top: clamp(2rem, 5vh, 62px);
	background-color: var(--azure-radiance, #007bff);
	border: none;
	border-radius: 36px;
	cursor: pointer;
	transition: transform 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease;
}

.submit-button:hover:not(:disabled) {
	transform: scale(1.02);
	box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.submit-button:focus-visible {
	outline: 2px solid var(--azure-radiance, #007bff);
	outline-offset: 2px;
}

.submit-button:disabled,
.submit-button.is-loading {
	opacity: 0.7;
	cursor: not-allowed;
	transform: none;
}

.submit-button--register {
	margin-top: clamp(1.5rem, 4vh, 55px);
}

.button-text {
	white-space: nowrap;
}

/* ========== АНИМАЦИЯ ПЕРЕКЛЮЧЕНИЯ ФОРМ ========== */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
		transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from {
	opacity: 0;
	transform: translateX(15px) scale(0.98);
}

.fade-leave-to {
	opacity: 0;
	transform: translateX(-15px) scale(0.98);
}

/* ========== АДАПТИВНОСТЬ — МАСШТАБИРОВАНИЕ ПОД VIEWPORT ========== */


/* QHD 2560x1440 (15.6") — компенсация под 100% масштаб (как при 90%) */
/* @media (min-width: 2560px) and (max-width: 2880px) {
	.auth-page {
		transform: scale(0.9);
		transform-origin: top left;
		height: calc(100vh / 0.9);
	}
} */

/* Планшеты и мобильные (< 1024px) — скрываем картинку, центрируем форму */
@media (max-width: 1023px) {
	.auth-page {
		min-width: 100%;
		width: 100%;
		min-height: 100vh;
		justify-content: center;
		padding: 2rem;
	}
	.auth-image {
		display: none;
	}
	.auth-container {
		position: relative;
		left: auto;
		top: auto;
		margin: 0 auto;
	}
	.back-button {
		position: fixed;
		top: 1rem;
		left: 1rem;
		z-index: 100;
	}
}

/* Мобильные (< 768px) */
@media (max-width: 767px) {
	.auth-page {
		padding: 1rem;
	}
	.auth-container {
		width: 100%;
		max-width: 400px;
	}
	.auth-content {
		width: 100%;
		margin-left: 0;
	}
	.submit-button {
		width: 100%;
		margin-left: 0;
	}
	:deep(.group-232),
	:deep(.group-2) {
		width: 100% !important;
		margin-left: 0 !important;
	}
	:deep(.rectangle-80),
	:deep(.input-field) {
		width: 100% !important;
	}
	.name-grid {
		grid-template-columns: 1fr;
	}
}

/* ========== ACCESSIBILITY ========== */
@media (prefers-reduced-motion: reduce) {
	.fade-enter-active,
	.fade-leave-active,
	.error-slide-enter-active,
	.error-slide-leave-active,
	.tab-indicator,
	.submit-button,
	.back-button {
		transition: none !important;
		animation: none !important;
	}
}

/* Высокий контраст */
@media (prefers-contrast: high) {
	.error-message {
		font-weight: 700;
	}
	.submit-button {
		border: 2px solid #000;
	}
}
</style>
